import { Module, Mutation, Action } from 'vuex-module-decorators'
import { find } from 'lodash'
import { Alert, Data, dataApi, defaultData, KanaBibliography } from '@/api'
import BaseStore from '@/store/BaseStore'
import { AloneSearchType, AddressCodeDialogInitialFocus } from '@/types'

@Module({
  name: 'DataStore',
  namespaced: true,
  stateFactory: true
})
export default class DataStore extends BaseStore {
  data: Data = defaultData()

  get customerCode(): string {
    return this.data.customer.code
  }

  get outputOptionDisabled(): boolean {
    return this.data.batch.reminderList.outputOptionDisabled
  }

  get findAlertByCode(): (code: string) => Alert {
    return code => {
      const item = find<Alert>(this.data.alerts, { code })
      if (item === undefined) {
        return {
          code: '',
          level: '',
          message: '',
          attribute: '',
          index: -1,
          subAttribute: ''
        }
      }
      return item
    }
  }

  get enabledTooli(): boolean {
    return this.data.tooli.enabled
  }

  get enableManagementInfo(): boolean {
    return this.data.app.enableManagementInfo
  }

  get receiptInfoLendBulkPrint(): boolean {
    return this.data.receiptInfo.lend.bulkPrint
  }

  get receiptInfoLendStatusTitle(): string {
    return this.data.receiptInfo.lendStatus.title
  }

  get receiptInfoLendStatusBranchNamePrefix(): string {
    return this.data.receiptInfo.lendStatus.branchNamePrefix
  }

  get receiptInfoLendStatusDisplayAuthor(): string {
    return this.data.receiptInfo.lendStatus.displayAuthor
  }

  get receiptInfoLendStatusDisplayReserveCount(): string {
    return this.data.receiptInfo.lendStatus.displayReserveCount
  }

  get receiptInfoLendStatusDueDateLabel(): string {
    return this.data.receiptInfo.lendStatus.dueDateLabel
  }

  get receiptInfoLendStatusUserNoMaskDigit(): number {
    return this.data.receiptInfo.lendStatus.userNoMaskDigit
  }

  get receiptInfoLendStatusCollectionPartitionCount(): number {
    return this.data.receiptInfo.lendStatus.collectionPartitionCount
  }

  get receiptInfoLendStatusAbbreviationUnitLabel(): string {
    return this.data.receiptInfo.lendStatus.abbreviationUnitLabel
  }

  get receiptInfoLendStatusTotalLabel(): string {
    return this.data.receiptInfo.lendStatus.totalLabel
  }

  get receiptInfoLendStatusTotalUnitLabel(): string {
    return this.data.receiptInfo.lendStatus.totalUnitLabel
  }

  get receiptInfoLendStatusTotalLabelFormatType(): string {
    return this.data.receiptInfo.lendStatus.totalLabelFormatType
  }

  get receiptInfoLendStatusNotices(): string[] {
    return this.data.receiptInfo.lendStatus.notices
  }

  get displayLendStatusLostCollection(): boolean {
    return this.data.receiptInfo.lendStatus.displayLostCollection === '1'
  }

  get receiptInfoReserveStatusUserNoMaskDigit(): number {
    return this.data.receiptInfo.reserveStatus.userNoMaskDigit
  }

  get groupLendOutputPrinter(): boolean {
    return this.data.receiptInfo.groupLendOutputPrinter
  }

  get kanaBibliographies(): KanaBibliography[] {
    return this.data.kana.bibliographies
  }

  get enabledIcTag(): boolean {
    return this.data.icTag.enabled
  }

  get enabledIcTagUid(): boolean {
    return this.data.icTag.enabled && this.data.icTag.uidEnabled
  }

  get displayLendPageWorkType(): boolean {
    return this.data.page.lend.displayWorkType === '1'
  }

  get displayLendPageGender(): boolean {
    return this.data.page.lend.displayGender === '1'
  }

  get displayLendPageUserName(): boolean {
    return this.data.page.lend.displayUserName === '1'
  }

  get displayLendPageReadingInsideButton(): boolean {
    return this.data.page.lend.displayReadingInside === '1'
  }

  get returnReadingCountDisplayType(): string {
    return this.data.page.return.readingCountDisplayType
  }

  get returnPageUserRelatedColumnIdDisplay(): boolean {
    return this.data.page.return.userIdButton === '0'
  }

  get returnPageUserColumn(): boolean {
    return this.data.page.return.userIdButton === '1'
  }

  get returnPageUserRelatedColumnNameDisplay(): boolean {
    return this.data.page.return.userIdButton === '2'
  }

  get displayReturnPageReturnBorrowToggleButton(): boolean {
    return this.data.page.return.displayReturnBorrow === '1'
  }

  get displayReturnPageUserRelatedColumnModal(): boolean {
    return this.data.page.return.userIdButtonUserDisplayType === '0'
  }

  get displayUserPageWorkType(): boolean {
    return this.data.page.user.displayWorkType === '1'
  }

  get isResetCardLostCountOnUserExpirationUpdate(): boolean {
    return this.data.page.user.resetCardLostCountOnExpirationUpdate === '1'
  }

  get displayCardLostCountUpdateAlertOnUserCardReissue(): boolean {
    return this.data.page.user.displayCardLostCountUpdateAlertOnReissue === '1'
  }

  get displayUserPageMyBranch(): boolean {
    return this.data.page.user.basicTab.displayMyBranch === '1'
  }

  get displayUserPageReserveReceivePlace(): boolean {
    return this.data.page.user.basicTab.displayReserveReceivePlace === '1'
  }

  get displayUserPageReserveContactMethod(): boolean {
    return this.data.page.user.basicTab.displayReserveContactMethod === '1'
  }

  get displayUserPageOpacSetting(): boolean {
    return this.data.page.user.basicTab.displayOpacSetting === '1'
  }

  get displayUserPageOther(): boolean {
    return this.data.page.user.basicTab.displayOther === '1'
  }

  get displayUserPageAddress(): boolean {
    return this.data.page.user.basicTab.displayAddress === '1'
  }

  get displayUserPageMailAddress(): boolean {
    return this.data.page.user.basicTab.displayMailAddress === '1'
  }

  get displayUserPageOtherContactTab(): boolean {
    return this.data.page.user.otherContactTab.display === '1'
  }

  get displayUserPageNewArrivalNoticeTab(): boolean {
    return this.data.page.user.newArrivalNoticeTab.display === '1'
  }

  get displayUserPageOldNameTab(): boolean {
    return this.data.page.user.oldNameTab.display === '1'
  }

  get displayUserPageRemindCountHistory(): boolean {
    return (
      this.data.page.user.usageSituationTab.displayRemindCountHistory === '1'
    )
  }

  get displayUserPageReserveCountHistory(): boolean {
    return (
      this.data.page.user.usageSituationTab.displayReserveCountHistory === '1'
    )
  }

  get displayUserPageExternalCollaborationTab(): boolean {
    return this.data.page.user.externalCollaborationTab.display === '1'
  }

  get displayShelfTypeTempChangePageDisplayHandlingType(): boolean {
    return this.data.page.shelfTypeTempChange.displayHandlingType === '1'
  }

  get displayShelfTypeTempChangeFixPageDisplayHandlingType(): boolean {
    return this.data.page.shelfTypeTempChangeFix.displayHandlingType === '1'
  }

  get industryCode(): string {
    return this.data.icTag.industryCode
  }

  get branchType(): string {
    return this.data.icTag.branchType
  }

  get agencyCodeCheck(): boolean {
    return this.data.icTag.agencyCodeCheck === '1'
  }

  get agencyCode(): string {
    return this.data.icTag.agencyCode
  }

  get acceptCollectionActualAfi(): boolean {
    return this.data.icTag.acceptCollectionActualAfi === '1'
  }

  get acceptCollectionMaintenanceIcWrite(): boolean {
    return this.data.icTag.acceptCollectionMaintenanceIcWrite === '1'
  }

  get acceptCollectionMaintenanceIcRead(): boolean {
    return this.data.icTag.acceptCollectionMaintenanceIcRead === '1'
  }

  get autoLibEnabled(): boolean {
    return this.data.autoLib.enabled
  }

  get closedShelfTypes(): string[] {
    return this.data.autoLib.closedShelfTypes
  }

  get autoLibRetrievalStationCode(): string {
    return this.data.autoLib.retrievalStationCode
  }

  get displayAdditionalSearch(): boolean {
    return this.data.page.userSearch.displayAdditionalSearch === '1'
  }

  get displayUserSearchPagePhone(): boolean {
    return this.data.page.userSearch.displayPhone === '1'
  }

  get displayUserSearchPageAddress(): boolean {
    return this.data.page.userSearch.displayAddress === '1'
  }

  get displayUserSearchPageMailAddress(): boolean {
    return this.data.page.userSearch.displayMailAddress === '1'
  }

  get enabledReWriteCard(): boolean {
    return this.data.rewritecard.enabled
  }

  get enabledMyNumberCard(): boolean {
    return this.data.mynumbercard.enabled
  }

  get displayModalReserveReserveUserNameColumn(): boolean {
    return this.data.page.reserve.displayReserveUserName === '1'
  }

  get displayModalReserveReserveMethod(): boolean {
    return this.data.page.reserve.displayReserveMethod === '1'
  }

  get displayModalReserveForwardingBranch(): boolean {
    return this.data.page.reserve.displayForwardingBranch === '1'
  }

  get displayModalReserveForwardingPlace(): boolean {
    return this.data.page.reserve.displayForwardingPlace === '1'
  }

  get displayModalReserveContactMethod(): boolean {
    return this.data.page.reserve.displayReserveContactMethod === '1'
  }

  get enabledTrcBellNewOrderRegistration(): boolean {
    return this.data.batch.trcBellNewOrderRegistration
  }

  get defaultBibliographyDetailTooliDownloadMaterialFormat(): string {
    return this.data.page.bibliographyDetail.tooliDownload.defaultMaterialFormat
      .bibliographyReal
  }

  get displayBibliographyDetailPageReserveUserName(): boolean {
    return this.data.page.bibliographyDetail.displayReserveUserName === '1'
  }

  get displayBibliographyDetailPageInterLibraryLoanCollectionRegisterButton(): boolean {
    return (
      this.data.page.bibliographyDetail.collectionOrderTab
        .displayInterLibraryLoanCollectionRegister === '1'
    )
  }

  get displayBibliographyDetailPageDisplayAcceptButton(): boolean {
    return (
      this.data.page.bibliographyDetail.collectionOrderTab.displayAccept === '1'
    )
  }

  get displayBibliographyDetailPageDisplayRegisterButton(): boolean {
    return (
      this.data.page.bibliographyDetail.collectionOrderTab.displayRegister ===
      '1'
    )
  }

  get displayBibliographyDetailPageDisplayOrderButton(): boolean {
    return (
      this.data.page.bibliographyDetail.collectionOrderTab.displayOrder === '1'
    )
  }

  get bibliographyDetailPageDisplayRemovedSwitchDefaultValue(): boolean {
    return (
      this.data.page.bibliographyDetail.collectionOrderTab
        .displayRemovedSwitchDefaultValue === '1'
    )
  }

  get displayBibliographyDetailPageSetNumber(): boolean {
    return (
      this.data.page.bibliographyDetail.collectionOrderTab.displaySetNumber ===
      '1'
    )
  }

  get displayBibliographyDetailPageBibliographyIntegrationButton(): boolean {
    return (
      this.data.page.bibliographyDetail.displayBibliographyIntegration === '1'
    )
  }

  get displayBibliographyDetailContentTab(): boolean {
    return this.data.page.bibliographyDetail.contentTab.display === '1'
  }

  get requiredNotationOrKanaBibliographyDetailContentTab(): boolean {
    return this.data.page.bibliographyDetail.contentTab.requiredType === '1'
  }

  get displayBibliographyDetailCopyRegisterButton(): boolean {
    return (
      this.data.page.bibliographyDetail.bibliographyTab.displayCopyRegister ===
      '1'
    )
  }

  get displayBibliographyDetailEditButton(): boolean {
    return this.data.page.bibliographyDetail.bibliographyTab.displayEdit === '1'
  }

  get displayBibliographySearchResultInterLibraryLoanCollectionRegisterButton(): boolean {
    return (
      this.data.page.bibliographySearchResult
        .displayInterLibraryLoanCollectionRegister === '1'
    )
  }

  get displayBibliographySearchResultInterLibraryLoanCollectionCreateButton(): boolean {
    return (
      this.data.page.bibliographySearchResult
        .displayInterLibraryLoanCollectionCreate === '1'
    )
  }

  get enabledFelica(): boolean {
    return this.data.felica.enabled
  }

  get enabledBarcodeReaderSound(): boolean {
    return this.data.app.barcodeReaderSoundEnabled
  }

  get insideReadingHeaderBarColorCode(): string {
    return this.data.page.lend.insideReadingHeaderBarColor
  }

  get displayReceiptOutputInsideReading(): boolean {
    return (
      this.data.page.lend.displayReceiptOutputInsideReading === '1' ||
      this.data.page.lend.displayReceiptOutputInsideReading === '2'
    )
  }

  get lendNavigationReceiptOutputDefaultValueOnInsideReading(): boolean {
    return this.data.page.lend.displayReceiptOutputInsideReading === '1'
  }

  get lendExtendReceiptOutput(): boolean {
    return this.data.page.lend.lendExtendReceiptOutput === '1'
  }

  get displayBibliographySearchInterLibraryLoanCollectionCreate(): boolean {
    return (
      this.data.page.bibliographySearch
        .displayInterLibraryLoanCollectionCreate === '1'
    )
  }

  get bibliographySearchAloneInitialSelectValue(): AloneSearchType {
    switch (this.data.page.bibliographySearch.alone.initialSelect) {
      case '1':
        return AloneSearchType.NewPublicationInformationDate
      case '2':
        return AloneSearchType.NewPublicationInformationVolume
      case '3':
        return AloneSearchType.CollectionNo
      case '4':
        return AloneSearchType.OrderId
      case '5':
        return AloneSearchType.Bibliography
      case '6':
        return AloneSearchType.Isbn
      case '7':
        return AloneSearchType.ProductNumber
      case '8':
        return AloneSearchType.MagazineCode
      case '9':
        return AloneSearchType.Marc
      default:
        // 一応設定しているが、状況的には想定外の処理
        return AloneSearchType.NewPublicationInformationDate
    }
  }

  get enabledSchool(): boolean {
    return this.data.app.schoolEnabled
  }

  get enabledRequestReserve(): boolean {
    return this.data.app.requestReserveEnabled
  }

  get headerBarColor(): string {
    return this.data.env.headerBarColor
  }

  // 現状、フォーカスの選択肢が二通りなので真偽値で表現
  get systemCodeDialogInitialFocusName(): boolean {
    return this.data.page.systemCodeDialog.initialFocusPosition === '1'
  }

  get addressCodeDialogInitialFocus(): AddressCodeDialogInitialFocus {
    return this.data.page.addressCodeDialog.initialFocusPosition
  }

  @Mutation
  public setData(data: Data) {
    this.data = data
  }

  @Action({ rawError: true })
  public async fetch() {
    const items = await dataApi.getData()
    this.setData(items)
  }
}
